import { getStoredTenantSlug } from '@/lib/tenant'

function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(objectUrl)
}

/**
 * Downloads a same-origin-fetchable URL with a chosen filename, regardless of whether the
 * server sends a Content-Disposition header — some storage backends (notably the local
 * GCS emulator) don't reliably honor it, so the filename can't be trusted to the server
 * alone.
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status}`)
  }

  downloadBlob(await response.blob(), filename)
}

/**
 * Like {@link downloadFile}, but for platform-api routes gated by
 * TenantResolverMiddleware. A plain anchor-click or `window.location` navigation can't
 * attach custom headers, so those routes need a real `fetch()` carrying credentials and
 * X-Tenant-Slug.
 */
export async function downloadAuthenticatedFile(
  url: string,
  filename: string,
): Promise<void> {
  const tenantSlug = getStoredTenantSlug()
  const response = await fetch(url, {
    credentials: 'include',
    headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
  })
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status}`)
  }

  downloadBlob(await response.blob(), filename)
}
